import { GraphQLError } from "graphql"
import { groq } from "next-sanity"

import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqShortProductProjection } from "../sanity/product.projection"

/**
 * Query for searching products.
 */
export const queryProductSearch = {
  /**
   * Resolver function for productSearch query.
   * @param {any} parent - The parent resolver.
   * @param {Object} args - The query arguments.
   * @param {Object} context - The resolver context.
   * @param {Object} info - The query info.
   * @returns {Object} - The search results.
   */
  async productSearch(parent, args, context, info) {
    const {
      offset = 0,
      limit = 25,
      sort,
      lang = AppSettings.defaultLocale(),
      cdn = true,
      sortWithinLimits = false,
      search,
      categories,
      vendor,
    } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.PRODUCTS,
      operation: Operations.READ,
    })
    setDefaults(search)

    const productSearchString = getProductSearchQueryString({ search })

    if (search && productSearchString === "") {
      throw new GraphQLError("Invalid search string for selected field", {})
    }

    const productFilter = await getFilterForProductAttributes({
      categories,
      vendor,
      client: context.sanityClient,
    })

    const products = await getProductsFromSanity({
      context,
      productFilter,
      offset,
      limit,
      sort,
      lang,
      cdn,
      sortWithinLimits,
      productSearchString,
      searchString: search.searchString,
    })

    return {
      ...products,
      offset,
      limit,
      count: products?.items?.length,
    }
  },
}

/**
 * Sets default search options if not provided.
 * @param {Object} search - The search options.
 */
function setDefaults(search) {
  if (search) {
    const shouldBeSearchedInAllFields = !search?.searchInFields || Object.keys(search.searchInFields).length === 0

    if (shouldBeSearchedInAllFields) {
      search.searchInFields = {
        shortDescription: true,
        key: true,
        title: true,
      }
    }
  }
}

/**
 * Generates the product search query string.
 * @param {Object} search - The search options.
 * @returns {string} - The product search query string.
 */
function getProductSearchQueryString({ search }) {
  if (!search?.searchString) return ""

  const stringMatcher = getProductMatchersForStingValues({ search })
  const numberMatcher = getProductMatchersForNumberValues({ search })

  const searchString = `${stringMatcher}${numberMatcher}`

  if (searchString !== "") {
    return `&& (${searchString})`
  }
  return ""
}

/**
 * Generates the matchers for number values in the product search query.
 * @param {Object} search - The search options.
 * @returns {string} - The matchers for number values.
 */
function getProductMatchersForNumberValues({ search }) {
  if (!includesNumbersOnly(search.searchString)) return ""
  const { searchInFields } = search
  const matchers = []
  if (searchInFields?.key) matchers.push(`key match '*'+$term+'*'`)

  return matchers.length ? `${matchers.join(" || ")} ` : ""
}

/**
 * Generates the matchers for string values in the product search query.
 * @param {Object} search - The search options.
 * @returns {string} - The matchers for string values.
 */
function getProductMatchersForStingValues({ search }) {
  if (includesNumbersOnly(search.searchString)) return ""
  const { searchInFields } = search
  const matchers = []

  if (searchInFields?.title) matchers.push(`title match '*'+$term+'*' `)
  if (searchInFields?.shortDescription) matchers.push(`shortDescription match '*'+$term+'*' `)

  return matchers.length ? `${matchers.join(" || ")} ` : ""
}

/**
 * Generates the filter for product attributes in the product search query.
 * @param {Object} options - The filter options.
 * @param {Array} options.categories - The categories to filter by.
 * @param {string} options.vendor - The vendor to filter by.
 * @param {Object} options.client - The Sanity client.
 * @returns {string} - The filter for product attributes.
 */
async function getFilterForProductAttributes({ categories, vendor, client }) {
  const filter = []
  if (categories?.length) {
    const categoriesIncludingSubcategories = await getSubcategories({ categories, client })
    const categoriesSearchString = categoriesIncludingSubcategories
      .map((category) => ` "${category}" in categories[]._ref `)
      .join("||")
    filter.push(`(${categoriesSearchString})`)
  }
  if (vendor) {
    filter.push(`vendor._ref == "${vendor}"`)
  }
  return filter.length ? "&& " + filter.join(" && ") : ""
}

/**
 * Retrieves products from Sanity based on the search options.
 * @param {Object} options - The options for retrieving products.
 * @param {Object} options.context - The resolver context.
 * @param {string} options.productFilter - The filter for product attributes.
 * @param {number} options.offset - The offset for pagination.
 * @param {number} options.limit - The limit for pagination.
 * @param {string} options.sort - The sort option.
 * @param {string} options.lang - The language option.
 * @param {boolean} options.cdn - The CDN option.
 * @param {boolean} options.sortWithinLimits - The sortWithinLimits option.
 * @param {string} options.productSearchString - The product search query string.
 * @param {string} options.searchString - The search string.
 * @returns {Object} - The retrieved products.
 */
async function getProductsFromSanity({
  context,
  productFilter,
  offset,
  limit,
  sort,
  lang,
  cdn,
  sortWithinLimits,
  productSearchString,
  searchString,
}) {
  const config = AppSettings.sanityConfigByLang(lang)
  const client = context.sanityClient
  client.config({ ...config, useCdn: cdn })

  const { pipesAndLimits, draftsFilter } = groqQueryOptions({
    sort,
    offset,
    limit,
    sortWithinLimits,
    includeDrafts: true,
  })

  const orphanArticlesFilter = `&& !(key match "orphan-article"+'*')`

  const totalCountQuery = groq`count(*[_type=="product"  ${productFilter} ${productSearchString}${draftsFilter}${orphanArticlesFilter}])`
  const productsQuery = groq`*[_type=="product" ${productFilter} ${productSearchString}${draftsFilter}${orphanArticlesFilter}] | score(
    boost(key match $term, 3),
    boost(title match $term, 2),
    boost(shortDescription match $term, 1)
  )${pipesAndLimits}
   {
    ...,
    ${groqShortProductProjection()}
  }`

  const [totalCount, productItems] = await Promise.all([
    client.fetch(totalCountQuery, { term: searchString }),
    client.fetch(productsQuery, { term: searchString }),
  ])

  const products = {
    total: totalCount,
    items: productItems,
  }

  return products
}

/**
 * Checks if a string includes only numbers.
 * @param {string} searchString - The search string.
 * @returns {boolean} - True if the string includes only numbers, false otherwise.
 */
function includesNumbersOnly(searchString) {
  return !isNaN(searchString)
}

/**
 * Retrieves the subcategories for the given categories.
 * @param {Object} options - The options for retrieving subcategories.
 * @param {Array} options.categories - The categories to retrieve subcategories for.
 * @param {Object} options.client - The Sanity client.
 * @returns {Array} - The subcategories.
 */
const getSubcategories = async ({ categories, client }) => {
  const fetchDescendantsForCategories = async ({ categories, client }) => {
    const fetchDescendants = async (categoryId) => {
      let descendants = []
      let queue = [categoryId]

      while (queue.length > 0) {
        // Fetch all categories whose parent is in the queue in a single request
        let categories = await client.fetch(`*[_type == "category" && parentCategory._ref in ${JSON.stringify(queue)}]`)
        queue = []

        for (let category of categories) {
          descendants.push(category._id)
          queue.push(category._id)
        }
      }

      return descendants
    }

    const promises = categories.map((category) => fetchDescendants(category))
    const results = await Promise.all(promises)

    return results
  }

  let descendants = await fetchDescendantsForCategories({ categories, client })
  descendants = [...new Set(descendants.flat())]
  return [...categories, ...descendants]
}
